<template>
  <Loading v-if="isLoading" :type="2" />
  <div v-else class="h-100">
    <h4 class="font-weight-normal">
      {{ "components.softwares.softwareErrorHistory.title" | i18n }}
    </h4>
    <h6 class="text-light font-weight-light pb-2">
      {{ "components.softwares.softwareErrorHistory.time" | i18n }}
    </h6>
    <canvas
      ref="myCanvas"
      id="graphicCanvas"
      class="canvas"
      height="120%"
    ></canvas>
    <div
      class="overlay d-flex position-absolute font-weight-light justify-content-center align-items-center text-white h-100 w-100"
      v-if="dataEmpty"
    >
      <div class="text-center">
        <div>
          <i class="icon exclamation circle"></i>
        </div>
        <div>
          {{ "components.charts.noDataOverlay" | i18n }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Loading from "../others/Loading.vue";
import { Chart } from "chart.js";
/**
 * @description Affiche les statistiques (erreurs et avertissements) d'un logiciel dans un diagramme
 * @author Francine Jin
 * @author Benjamin Caron
 * @author Philippe Ménard
 * @vue-prop {Number} softwareId - L'id du logiciel dont on veut afficher les erreurs
 * @vue-data {Boolean} isLoading - Si la page est en train de charger
 * @vue-data {Array} monthNames - Liste contenant le nom des mois en lettres
 * @vue-data {Object} chart - Représentation des données reçues sous forme de graphique
 * @vue-data {Boolean} dataEmpty - Si les données sont vides
 */
export default {
  name: "SoftwareErrorHistory",
  components: {
    Loading
  },
  props: {
    softwareId: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      isLoading: true,
      monthNames: [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre"
      ],
      chart: null,
      dataEmpty: false
    };
  },
  methods: {
    /**
     * @description Fonction qui donne le bon dataset selon les données envoyées
     * @returns {Array} Les datasets pour le graphique
     */
    async generateDatasets() {
      let datasets = [];
      let data = [await this.getErrors(), await this.getWarning()];
      this.dataEmpty =
        data[0].reduce((a, b) => a + b, 0) +
          data[1].reduce((a, b) => a + b, 0) ===
        0;
      let colors = [
        getComputedStyle(document.body).getPropertyValue("--danger"),
        getComputedStyle(document.body).getPropertyValue("--warning")
      ];
      let labels;
      try {
        labels = [
          this.$i18n.t("components.softwares.softwareErrorHistory.error"),
          this.$i18n.t("components.softwares.softwareErrorHistory.warning")
        ];
      } catch (e) {
        if (typeof e != TypeError) {
          throw e;
        }
      }
      for (let i = 0; i < 2; i++) {
        datasets.push({
          data: data[i],
          backgroundColor: colors[i],
          hoverBackgroundColor: colors[i],
          label: labels[i],
          barPercentage: 0.6
        });
      }
      this.isLoading = false;
      return datasets;
    },
    /**
     * @description Fonction qui renvoie la liste du nombre d'erreurs critiques par mois
     * @returns {Array} La liste du nombre d'erreurs par mois
     */
    async getErrors() {
      this.refreshDone = false;
      let res = await this.$api.getSoftwareErrorsCritics(this.softwareId); // le paramètre sera l'id du logiciel
      return res.data;
    },
    /**
     * @description Fonction qui renvoie la liste du nombre d'avertissements par mois
     * @returns {Array} La liste du nombre d'avertissements par mois
     */
    async getWarning() {
      this.refreshDone = false;
      let res = await this.$api.getSoftwareWarning(this.softwareId); // le paramètre sera l'id du logiciel
      return res.data;
    },
    /**
     * @description Fonction qui donne les noms des x derniers mois
     * @param {Number} amount Le nombre de mois demandés
     * @returns {Array} Le nom des mois demandés
     */
    getLastMonths(amount) {
      var currentMonth = new Date().getMonth();
      var lastMonths = [];
      for (let i = amount; i--; i < 0) {
        let month = currentMonth - i;
        if (month < 0) {
          month = 12 + month;
        }
        lastMonths.push(this.monthNames[month]);
      }
      return lastMonths;
    },
    /**
     * @description Fonction qui génère le graphique dans le canvas
     */
    async generateChart() {
      const datasets = await this.generateDatasets();
      this.chart = new Chart(this.$refs.myCanvas, {
        responsive: true,
        type: "bar",
        data: {
          labels: this.getLastMonths(6),
          datasets: datasets
        },
        options: {
          legend: {
            display: window.innerWidth > 414
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  precision: 0
                }
              }
            ]
          }
        }
      });
    },
    /**
     * @description Fonction qui rafraîchit les graphiques
     */
    refreshGraphics() {
      this.generateDatasets();
      this.generateChart();
    }
  },
  /**
   * @description Fonction qui génère le graphique au chargement de la page
   */
  mounted() {
    this.refreshGraphics();
  }
};
</script>

<style lang="scss" scoped>
.overlay {
  border-radius: $border-radius;
  font-size: 1.25em;
  background: rgba(0, 0, 0, 0.35);
  top: 0;
  left: 0;
}
</style>
